import { useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import {
  Icon,
  PageFrame,
  PageHeader,
  WS_COLOR,
  WS_RADIUS,
  WS_STATUS,
  WorkspaceThemeProvider,
  icons,
} from '@platform/ui-kit/workspace';
import type { ModuleSection } from '@platform/entitlements';

/**
 * AI Readiness → Start Menu, as a Decision Workspace screen.
 *
 * Shown with `?ui=workspace` next to the ported original in `StartPage.tsx`, which stays the
 * default until the owner approves this one. The Start Menu is the most protected screen in the
 * product (owner-approved 2026-08-04), so this is offered for review and replaces nothing.
 *
 * WHAT IS UNCHANGED: every word of the copy (greeting, headline, lede, the two paths, the five
 * steps with their titles, qualifiers, descriptions and tools, the express lane, the support
 * strip and its mailto), the three views (welcome, journey, express), step 1 open on arrival,
 * one step open at a time, tools without a screen shown but inert, and where a live tool goes.
 *
 * WHAT CHANGES, by the design system and nothing else: the page frame (locked header, the body is
 * the one scroll region), APR's gradient hero replaced by the standard header, the two paths as
 * two equal choices, steps as a disclosure list with `aria-expanded`, tiles as buttons in the kit
 * type, and the "Back to welcome screen" link as a header button.
 */

const ADVISOR_MAILTO =
  'mailto:support@talogent.com' +
  '?subject=' +
  encodeURIComponent('AI Readiness — request for assistance') +
  '&body=' +
  encodeURIComponent(
    'Tell us what you need help with and we will get back to you.\n\n' +
      '(Sent from AI Readiness)\n',
  );

interface Tool {
  title: string;
  description: string;
  path?: string;
}

interface Step {
  n: number;
  title: string;
  qualifier?: string;
  description: string;
  tools: Tool[];
}

/** The five steps, copy verbatim from the source (as in StartPage.tsx). */
const STEPS: readonly Step[] = [
  {
    n: 1,
    title: 'Score — objectively',
    description:
      "Capture every initiative and score it against one consistent framework, so priority reflects evidence, not the loudest voice in the room. You get an unbiased ranking of what's genuinely worth doing.",
    tools: [
      { title: 'Opportunities', description: 'Capture & score initiatives' },
      { title: 'Scoring Framework', description: 'Dimensions, criteria & weights' },
      {
        title: 'Strategic Intelligence',
        description: 'Discovery & unbiased analysis',
        path: '/ai_readiness/strategic-intelligence',
      },
    ],
  },
  {
    n: 2,
    title: 'Weigh the benefit',
    description:
      'Put a number on the business value each opportunity delivers — savings, ROI, and the value it realizes over time — so a go/no-go is defensible rather than a gut call.',
    tools: [
      { title: 'Benefits & ROI', description: 'Value & ROI rollups' },
      { title: 'Portfolio Optimizer', description: 'Value scenarios & trade-offs' },
    ],
  },
  {
    n: 3,
    title: 'Weigh the cost',
    description:
      'Model what each initiative costs to build and to run — MVP cost, discounted cash flow, and the resource economics per phase — then set benefit against cost to decide the disposition.',
    tools: [
      { title: 'MVP Cost', description: 'Build-cost scenarios' },
      { title: 'NPV Model', description: 'Discounted cash flow' },
      { title: 'Delivery Economics', description: 'Cost per phase & mix' },
      { title: 'Disposition', description: 'Benefit vs. cost, decided' },
    ],
  },
  {
    n: 4,
    title: 'Shape the roadmap',
    description:
      "Sequence the work you've chosen into a high-level implementation roadmap — phases, dependencies and timelines — and see the whole portfolio's shape before you commit a single team.",
    tools: [
      { title: 'Roadmap Planning', description: 'Sequence phases & dependencies' },
      { title: 'Planning Dashboard', description: 'Score, cost & value at a glance' },
    ],
  },
  {
    n: 5,
    title: 'Run delivery',
    qualifier: 'Agile or Waterfall',
    description:
      'Take the roadmap into delivery and run it your way — define the programs, lock the timeline, then track progress, budget and effort against plan, all the way to sign-off.',
    tools: [
      { title: 'Roadmap Definition', description: 'Programs, opportunities & phases' },
      { title: 'Timeline Finalization', description: 'Lock phase dates' },
      { title: 'Execution Control Center', description: 'Progress & delivery status' },
      { title: 'Execution Dashboard', description: 'Budget, effort & delivery health' },
    ],
  },
];

const EXPRESS: ReadonlyArray<{ n: string; t: string; d: string }> = [
  {
    n: 'Step 1',
    t: 'Import your roadmap',
    d: 'Upload a Waterfall or Agile template on Roadmap Definition — the delivery program, phases and timeline are created in one shot.',
  },
  {
    n: 'Step 2',
    t: 'Run delivery',
    d: 'Track progress, budget and effort in the Execution Control Center, and report status — Agile or Waterfall.',
  },
];

const INERT_NOTE =
  'Tools shown without a link belong to modules that are not migrated yet. They appear here ' +
  "because this is the product's own journey; each becomes live as its module lands.";

export interface AiReadinessStartPageProps {
  firstName?: string;
  sections?: readonly ModuleSection[];
}

export function AiReadinessStartPageWorkspace(props: AiReadinessStartPageProps) {
  return (
    <WorkspaceThemeProvider baseline={false}>
      <Screen {...props} />
    </WorkspaceThemeProvider>
  );
}

const CARD_SX = {
  border: `1px solid ${WS_COLOR.border}`,
  borderRadius: `${WS_RADIUS.card}px`,
  backgroundColor: WS_COLOR.surface,
} as const;

const EYEBROW_SX = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: WS_COLOR.textMuted,
} as const;

function BlockLabel({ children, count }: { children: string; count?: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 6, mb: 3 }}>
      <Typography variant="h2" component="h2">
        {children}
      </Typography>
      <Box aria-hidden sx={{ flex: 1, height: 1, backgroundColor: WS_COLOR.border }} />
      {count ? (
        <Typography variant="caption" sx={{ color: WS_COLOR.textMuted }}>
          {count}
        </Typography>
      ) : null}
    </Box>
  );
}

function ToolTiles({ tools, go }: { tools: readonly Tool[]; go: (path: string) => void }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 3,
        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
      }}
    >
      {tools.map((t) => (
        <Box
          key={t.title}
          component="button"
          type="button"
          disabled={!t.path}
          onClick={t.path ? () => go(t.path!) : undefined}
          sx={{
            ...CARD_SX,
            font: 'inherit',
            textAlign: 'left',
            p: 4,
            minHeight: 72,
            cursor: t.path ? 'pointer' : 'default',
            color: t.path ? WS_COLOR.text : WS_COLOR.textMuted,
            '&:hover:not(:disabled)': {
              backgroundColor: WS_STATUS.info.fill,
              borderColor: WS_STATUS.info.fill,
            },
          }}
        >
          <Typography component="span" sx={{ display: 'block', fontWeight: 600, color: 'inherit' }}>
            {t.title}
          </Typography>
          <Typography
            component="span"
            variant="body2"
            sx={{ display: 'block', color: WS_COLOR.textMuted }}
          >
            {t.description}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function Screen({ firstName }: AiReadinessStartPageProps) {
  const navigate = useNavigate();
  // Stay in the workspace version when a tool is opened: the switch lives in the query string.
  const { search } = useLocation();
  const go = (path: string) => navigate(`${path}${search}`);
  const [view, setView] = useState<'welcome' | 'journey' | 'express'>('welcome');
  const [open, setOpen] = useState<number | null>(1);

  const liveTools = STEPS.flatMap((s) => s.tools).filter((t) => t.path);
  const anyInert = STEPS.some((s) => s.tools.some((t) => !t.path));
  const back = (
    <Button
      variant="tonal"
      onClick={() => setView('welcome')}
      startIcon={<Icon icon={icons.ArrowLeft} size={16} />}
    >
      Back to welcome screen
    </Button>
  );
  const hello = firstName || 'there';

  const frame = (header: ReactNode, body: ReactNode) => (
    <PageFrame header={header}>
      <Box tabIndex={0} sx={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: 'auto', pb: 6 }}>
        <Box sx={{ maxWidth: 1040 }}>{body}</Box>
      </Box>
    </PageFrame>
  );

  if (view === 'welcome') {
    return frame(
      <PageHeader
        id={`Welcome, ${hello}`}
        title="From raw idea to running delivery."
        subtitle="Pick how you want to work. Everything else follows one honest line — score what matters, weigh its value and cost, shape a roadmap, then deliver it. No wall of buttons to hunt through."
      />,
      <>
        <Box
          sx={{
            display: 'grid',
            gap: 4,
            mt: 2,
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          }}
        >
          <PathCard
            primary
            mode="The full journey · recommended"
            title="Start from scratch"
            lead="You have opportunities, but no plan yet. Let the platform score your initiatives objectively, quantify the benefit and the cost, shape a high-level roadmap, then take it into delivery."
            go="Follow the five steps"
            onClick={() => setView('journey')}
          />
          <PathCard
            mode="The express lane"
            title="Bring your own plan"
            lead="You already have a finalized roadmap. Skip the analysis. Import your Waterfall or Agile plan and go straight to execution — define delivery, track it, report it."
            go="Import & execute"
            onClick={() => setView('express')}
          />
        </Box>

        <BlockLabel>Jump straight to</BlockLabel>
        <ToolTiles tools={liveTools} go={go} />

        <Box
          sx={{
            ...CARD_SX,
            mt: 6,
            p: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 4,
            flexWrap: 'wrap',
          }}
        >
          <Typography sx={{ flex: '1 1 320px' }}>
            <b>Need Assistance?</b> Our team runs working sessions with you — the platform guides
            you, and you&apos;re never on your own.
          </Typography>
          <Button variant="tonal" component="a" href={ADVISOR_MAILTO}>
            Contact your AI Readiness advisor
          </Button>
        </Box>
      </>,
    );
  }

  if (view === 'express') {
    return frame(
      <PageHeader
        id="The express lane"
        title="You already have the plan — let's execute it."
        actions={back}
      />,
      <>
        <Box component="ol" sx={{ listStyle: 'none', m: 0, mt: 2, p: 0, display: 'grid', gap: 4 }}>
          {EXPRESS.map((s) => (
            <Box component="li" key={s.n} sx={{ ...CARD_SX, p: 4 }}>
              <Typography sx={EYEBROW_SX}>{s.n}</Typography>
              <Typography variant="h3" component="h2" sx={{ mt: 0.5 }}>
                {s.t}
              </Typography>
              <Typography sx={{ mt: 1 }}>{s.d}</Typography>
            </Box>
          ))}
        </Box>
        {anyInert ? (
          <Typography variant="body2" sx={{ mt: 4, color: WS_COLOR.textMuted }}>
            {INERT_NOTE}
          </Typography>
        ) : null}
      </>,
    );
  }

  return frame(
    <PageHeader
      id="Your guided journey"
      title={`Welcome, ${hello} — here's how AI Readiness works, step by step`}
      subtitle={
        <>
          Not sure? <b>Start from scratch</b> is the complete method. You can switch an opportunity
          to the express lane any time before it enters delivery.
        </>
      }
      actions={back}
    />,
    <>
      <BlockLabel count="5 steps">The full journey</BlockLabel>
      <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 3 }}>
        {STEPS.map((step) => {
          const isOpen = open === step.n;
          const bodyId = `air-step-${step.n}`;
          return (
            <Box component="li" key={step.n} sx={CARD_SX}>
              <Box
                component="button"
                type="button"
                aria-expanded={isOpen}
                aria-controls={bodyId}
                onClick={() => setOpen(isOpen ? null : step.n)}
                sx={{
                  all: 'unset',
                  boxSizing: 'border-box',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 4,
                  p: 4,
                  cursor: 'pointer',
                  borderRadius: `${WS_RADIUS.card}px`,
                  '&:hover': { backgroundColor: WS_COLOR.surfaceSubtle },
                  '&:focus-visible': {
                    outline: `2px solid ${WS_COLOR.primary}`,
                    outlineOffset: -2,
                  },
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    flex: 'none',
                    width: 32,
                    height: 32,
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: '50%',
                    fontWeight: 700,
                    backgroundColor: WS_STATUS.info.fill,
                    color: WS_STATUS.info.ink,
                  }}
                >
                  {step.n}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h3" component="h3">
                    {step.title}
                    {step.qualifier ? (
                      <Box component="span" sx={{ fontWeight: 400, color: WS_COLOR.textMuted }}>
                        {' '}
                        {step.qualifier}
                      </Box>
                    ) : null}
                  </Typography>
                  <Typography sx={{ mt: 0.5 }}>{step.description}</Typography>
                </Box>
                <Icon icon={isOpen ? icons.ChevronDown : icons.ChevronRight} />
              </Box>
              {isOpen ? (
                <Box id={bodyId} sx={{ px: 4, pb: 4, pt: 1 }}>
                  <Typography sx={{ ...EYEBROW_SX, mb: 2 }}>What&apos;s in here</Typography>
                  <ToolTiles tools={step.tools} go={go} />
                </Box>
              ) : null}
            </Box>
          );
        })}
      </Box>
      {anyInert ? (
        <Typography variant="body2" sx={{ mt: 4, color: WS_COLOR.textMuted }}>
          {INERT_NOTE}
        </Typography>
      ) : null}
    </>,
  );
}

function PathCard({
  mode,
  title,
  lead,
  go,
  primary,
  onClick,
}: {
  mode: string;
  title: string;
  lead: string;
  go: string;
  primary?: boolean;
  onClick: () => void;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        ...CARD_SX,
        font: 'inherit',
        color: WS_COLOR.text,
        textAlign: 'left',
        p: 5,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        borderWidth: primary ? 2 : 1,
        borderColor: primary ? WS_COLOR.primary : WS_COLOR.border,
        '&:hover': { backgroundColor: WS_STATUS.info.fill },
      }}
    >
      <Typography sx={EYEBROW_SX}>{mode}</Typography>
      <Typography variant="h2" component="h2">
        {title}
      </Typography>
      <Typography component="span">{lead}</Typography>
      <Box
        component="span"
        sx={{
          mt: 'auto',
          pt: 2,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1.5,
          fontWeight: 600,
          color: WS_COLOR.primary,
        }}
      >
        {go} <Icon icon={icons.ChevronRight} size={16} />
      </Box>
    </Box>
  );
}
